import { useEffect, useRef } from "react";

import type { BoardInbox } from "../infrastructure/api/EmdrBoard.api";

/**
 * Sends one message after another, never two at once.
 *
 * The mailbox numbers messages as they arrive. Two requests in flight together
 * can be committed in either order, and a reader that has seen the later one
 * skips the earlier for good — a "stop" overtaken by the "running" before it
 * would leave the dot moving. One side's messages go out in order instead.
 */
export function createSerialQueue() {
    let tail: Promise<unknown> = Promise.resolve();
    return <T>(task: () => Promise<T>): Promise<T> => {
        const run = tail.then(task, task);
        tail = run.catch(() => undefined);
        return run;
    };
}

/** How often each side asks the mailbox for what is new. */
export const RELAY_POLL_MS = 1000;
/** After a failed poll, wait longer before the next: the server may be struggling. */
const RELAY_RETRY_MS = 3000;

type Options = {
    /** Where to start reading; null keeps the poll off. */
    cursor: number | null;
    fetch: (after: number) => Promise<BoardInbox>;
    onMessage: (payload: string, sender: string) => void;
    onPresence?: (present: boolean) => void;
    onError?: () => void;
};

/**
 * Reads a shared board's mailbox for as long as there is a cursor.
 *
 * One request at a time — the next is scheduled when the last has answered —
 * so a slow server is asked less often rather than piled onto.
 */
export function useRelayPoll({ cursor, fetch, onMessage, onPresence, onError }: Options) {
    const handlers = useRef({ fetch, onMessage, onPresence, onError });
    handlers.current = { fetch, onMessage, onPresence, onError };

    useEffect(() => {
        if (cursor === null) return;
        let after = cursor;
        let stopped = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const tick = async () => {
            let delay = RELAY_POLL_MS;
            try {
                const inbox = await handlers.current.fetch(after);
                if (stopped) return;
                for (const message of inbox.messages) handlers.current.onMessage(message.payload, message.sender);
                after = Math.max(after, inbox.cursor);
                handlers.current.onPresence?.(inbox.present);
            } catch {
                if (stopped) return;
                handlers.current.onError?.();
                delay = RELAY_RETRY_MS;
            }
            if (!stopped) timer = setTimeout(() => void tick(), delay);
        };
        void tick();

        return () => {
            stopped = true;
            if (timer) clearTimeout(timer);
        };
    }, [cursor]);
}
